/**
 * TeamStrength Hub - Onboarding Page
 * Profile setup wizard after signup
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import { profileApi, modelApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Users, User, Briefcase, Sparkles, ArrowRight, ArrowLeft, Check,
  Camera, Trash2, Upload, Image
} from 'lucide-react';

const OnboardingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, currentOrg, checkAuth, organizations } = useAuth();
  const fileInputRef = useRef(null);
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Profile data
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [picture, setPicture] = useState('');
  
  // Strengths data
  const [model, setModel] = useState(null);
  const [selectedStrengths, setSelectedStrengths] = useState([]);
  
  const orgId = location.state?.orgId || currentOrg?.org_id || organizations?.[0]?.org_id;

  useEffect(() => {
    // Pre-fill from user data
    if (user) {
      setFirstName(user.first_name || user.name?.split(' ')[0] || '');
      setLastName(user.last_name || user.name?.split(' ').slice(1).join(' ') || '');
      setJobTitle(user.job_title || '');
      setPicture(user.picture || '');
    }
  }, [user]);

  // Handle file upload for profile picture
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    // Convert to base64 for preview and storage
    const reader = new FileReader();
    reader.onload = (e) => {
      setPicture(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    // Fetch strength model when on step 3
    const fetchModel = async () => {
      if (step === 3 && orgId) {
        try {
          const response = await modelApi.get(orgId);
          setModel(response.data);
        } catch (error) {
          console.error('Failed to load model', error);
        }
      }
    };
    fetchModel();
  }, [step, orgId]);

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      await profileApi.update({
        first_name: firstName,
        last_name: lastName,
        job_title: jobTitle,
        picture: picture
      });
      toast.success('Profile updated!');
      setStep(3);
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveStrengths = async () => {
    if (selectedStrengths.length < 5) {
      toast.error('Please select at least 5 strengths');
      return;
    }

    setLoading(true);
    try {
      await profileApi.saveStrengths({
        org_id: orgId,
        strengths: selectedStrengths.map((s, i) => ({ talent_id: s.talent_id, rank: i + 1 }))
      });
      await profileApi.complete();
      await checkAuth();
      toast.success('Setup complete!');
      // Navigate to profile page to see AI analysis
      navigate('/dashboard/profile');
    } catch (error) {
      toast.error('Failed to save strengths');
    } finally {
      setLoading(false);
    }
  };

  const addStrength = (talent) => {
    if (selectedStrengths.length >= 10) {
      toast.error('Maximum 10 strengths');
      return;
    }
    if (selectedStrengths.find(s => s.talent_id === talent.talent_id)) {
      return;
    }
    setSelectedStrengths([...selectedStrengths, talent]);
  };

  const removeStrength = (talentId) => {
    setSelectedStrengths(selectedStrengths.filter(s => s.talent_id !== talentId));
  };

  const moveStrength = (talentId, direction) => {
    const index = selectedStrengths.findIndex(s => s.talent_id === talentId);
    if (index === -1) return;
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === selectedStrengths.length - 1) return;
    
    const newArr = [...selectedStrengths];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    [newArr[index], newArr[swapIndex]] = [newArr[swapIndex], newArr[index]];
    setSelectedStrengths(newArr);
  };

  const skipOnboarding = async () => {
    // Mark profile as complete so user isn't forced back to onboarding
    try {
      await profileApi.complete();
      toast.info('You can complete your profile later from settings');
      await checkAuth();
      navigate('/dashboard/teams');
    } catch (error) {
      console.error('Skip error:', error);
      toast.error('Error skipping onboarding');
    }
  };

  // If user already completed profile, redirect to dashboard
  useEffect(() => {
    if (user?.profile_completed) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const totalSteps = model?.model ? 3 : 2;
  const progress = (step / totalSteps) * 100;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-gradient mb-4">
            <Users className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Manrope' }}>
            Complete Your Profile
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Step {step} of {totalSteps}
          </p>
        </div>

        {/* Progress */}
        <Progress value={progress} className="mb-8 h-2" />

        {/* Step 1: Welcome */}
        {step === 1 && (
          <Card className="animate-fade-in">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope' }}>
                Welcome! 👋
              </CardTitle>
              <CardDescription className="text-base">
                Let's set up your profile so your team can learn more about you
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Profile Photo Upload */}
              <div className="flex flex-col items-center gap-4">
                <div className="relative group">
                  <div className="w-32 h-32 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center overflow-hidden border-4 border-white dark:border-slate-800 shadow-lg">
                    {picture ? (
                      <img src={picture} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-16 h-16 text-violet-400" />
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <button 
                    className="absolute bottom-0 right-0 w-10 h-10 rounded-full bg-violet-600 hover:bg-violet-700 text-white flex items-center justify-center shadow-lg transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="upload-photo-btn"
                  >
                    <Camera className="w-5 h-5" />
                  </button>
                  {picture && (
                    <button 
                      className="absolute top-0 right-0 w-8 h-8 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => setPicture('')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Profile Photo</p>
                  <p className="text-xs text-slate-500">Click the camera to upload</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>First Name *</Label>
                  <Input
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="John"
                    required
                    data-testid="onboard-firstname"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Last Name *</Label>
                  <Input
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Doe"
                    required
                    data-testid="onboard-lastname"
                  />
                </div>
              </div>

              <div className="flex justify-between pt-4">
                <Button variant="ghost" onClick={skipOnboarding}>
                  Skip for now
                </Button>
                <Button 
                  className="bg-violet-600 hover:bg-violet-700"
                  onClick={() => setStep(2)}
                  data-testid="onboard-next-1"
                >
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Job Info */}
        {step === 2 && (
          <Card className="animate-fade-in">
            <CardHeader className="text-center">
              <div className="w-12 h-12 rounded-full bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center mx-auto mb-4">
                <Briefcase className="w-6 h-6 text-cyan-600" />
              </div>
              <CardTitle className="text-xl" style={{ fontFamily: 'Manrope' }}>
                What do you do?
              </CardTitle>
              <CardDescription>
                Help your team understand your role
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Job Title</Label>
                <Input
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  placeholder="e.g., Product Manager, Software Engineer, Designer"
                  data-testid="onboard-jobtitle"
                />
              </div>

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setStep(1)}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button 
                  className="bg-violet-600 hover:bg-violet-700"
                  onClick={handleSaveProfile}
                  disabled={loading}
                  data-testid="onboard-next-2"
                >
                  {loading ? 'Saving...' : model?.model ? 'Continue' : 'Complete Setup'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Strengths Selection */}
        {step === 3 && model?.model && (
          <Card className="animate-fade-in">
            <CardHeader className="text-center">
              <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-6 h-6 text-emerald-600" />
              </div>
              <CardTitle className="text-xl" style={{ fontFamily: 'Manrope' }}>
                Select Your Top Strengths
              </CardTitle>
              <CardDescription>
                Choose 5-10 strengths that best describe you, then rank them in order
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                {/* Available strengths */}
                <div>
                  <Label className="mb-2 block text-sm font-medium">Available Strengths</Label>
                  <ScrollArea className="h-[350px] border rounded-lg p-3">
                    {model?.categories?.map((category) => (
                      <div key={category.category_id} className="mb-4">
                        <p 
                          className="text-xs font-semibold uppercase tracking-wider mb-2"
                          style={{ color: category.color }}
                        >
                          {category.name}
                        </p>
                        <div className="space-y-1">
                          {model?.talents
                            ?.filter(t => t.category_id === category.category_id)
                            .map((talent) => {
                              const isSelected = selectedStrengths.find(s => s.talent_id === talent.talent_id);
                              return (
                                <button
                                  key={talent.talent_id}
                                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                                    isSelected 
                                      ? 'bg-slate-200 dark:bg-slate-700 opacity-50 cursor-not-allowed' 
                                      : 'hover:bg-slate-100 dark:hover:bg-slate-800'
                                  }`}
                                  onClick={() => !isSelected && addStrength(talent)}
                                  disabled={isSelected}
                                >
                                  {talent.name}
                                </button>
                              );
                            })}
                        </div>
                      </div>
                    ))}
                  </ScrollArea>
                </div>

                {/* Selected strengths */}
                <div>
                  <Label className="mb-2 block text-sm font-medium">
                    Your Top Strengths ({selectedStrengths.length}/10)
                  </Label>
                  <ScrollArea className="h-[350px] border rounded-lg p-3">
                    {selectedStrengths.length > 0 ? (
                      <div className="space-y-2">
                        {selectedStrengths.map((talent, index) => (
                          <div 
                            key={talent.talent_id}
                            className="flex items-center gap-2 p-2 rounded-lg bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800"
                          >
                            <span className="w-6 h-6 rounded-full bg-violet-600 text-white text-xs font-bold flex items-center justify-center shrink-0">
                              {index + 1}
                            </span>
                            <span className="flex-1 text-sm font-medium truncate">
                              {talent.name}
                            </span>
                            <div className="flex items-center gap-1 shrink-0">
                              <button
                                className="w-6 h-6 rounded hover:bg-violet-100 dark:hover:bg-violet-800 flex items-center justify-center text-xs"
                                onClick={() => moveStrength(talent.talent_id, 'up')}
                                disabled={index === 0}
                              >
                                ↑
                              </button>
                              <button
                                className="w-6 h-6 rounded hover:bg-violet-100 dark:hover:bg-violet-800 flex items-center justify-center text-xs"
                                onClick={() => moveStrength(talent.talent_id, 'down')}
                                disabled={index === selectedStrengths.length - 1}
                              >
                                ↓
                              </button>
                              <button
                                className="w-6 h-6 rounded hover:bg-red-100 dark:hover:bg-red-900/30 flex items-center justify-center text-red-500"
                                onClick={() => removeStrength(talent.talent_id)}
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full text-slate-400">
                        <Sparkles className="w-8 h-8 mb-2" />
                        <p className="text-sm">Click strengths to add them</p>
                      </div>
                    )}
                  </ScrollArea>
                </div>
              </div>

              <div className="flex justify-between pt-6">
                <Button variant="outline" onClick={() => setStep(2)}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button 
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={handleSaveStrengths}
                  disabled={loading || selectedStrengths.length < 5}
                  data-testid="onboard-complete"
                >
                  {loading ? 'Saving...' : 'Complete Setup'}
                  <Check className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default OnboardingPage;
